import { Formik } from "formik";
import ErrorBox from "lib/components/common/error-box";
import SimpleCard from "lib/components/common/simple-card";
import SimpleTextbox from "lib/components/common/simple-textbox";
import { REQUIRED_FIELD_VALIDATION_SCHEMA } from "lib/constants/validation-constants";
import { FC, useRef } from "react";
import { Row, Col, Button, Form } from "react-bootstrap";
import { toast } from "sonner";

interface PackageDetailsProps {
  error?: string;
  isLoading?: boolean;
  membershipPackageData: MembershipPackage;
  updateMembershipPackage: (membershipPackageData: MembershipPackage) => void;
  onCancelClick?: () => void;
  addMembershipPackage?: (membershipPackageData: MembershipPackage) => void;
  deleteMembershipPackage?: (membershipPackageId: string) => void;
}

const MembershipPackageDetails: FC<PackageDetailsProps> = ({
  error,
  isLoading,
  membershipPackageData,
  onCancelClick,
  updateMembershipPackage,
  addMembershipPackage,
  deleteMembershipPackage,
}) => {
  const formikRef = useRef(null);

  return (
    <SimpleCard
      title="Membership Package Details"
      isLoading={isLoading}
      action={
        <Button
          type="button"
          variant="outline-primary"
          className="px-4"
          onClick={() => {
            formikRef.current?.submitForm();
          }}
        >
          Save Membership Package
        </Button>
      }
      isShowBackButton
      onBackButtonClick={onCancelClick}
      error={error}
    >
      <Row>
        <Col sm="12">
          <Formik
            initialValues={
              {
                ...membershipPackageData,
              } as MembershipPackage
            }
            onSubmit={(values: MembershipPackage, { setSubmitting }) => {
              if (!values.name) {
                toast.error("Please enter package name");
                return;
              }

              if (!values.description) {
                toast.error("Please enter package description");
                return;
              }

              if (values.credits < 0) {
                toast.error("Credits cannot be negative");
                return;
              }

              if (values.price < 0) {
                toast.error("Price cannot be negative");
                return;
              }

              const formattedValues = {
                ...values
              };

              if (!membershipPackageData.id) {
                addMembershipPackage(formattedValues);
              } else {
                updateMembershipPackage(formattedValues);
              }
              setSubmitting(false);
            }}
            innerRef={formikRef}
          >
            {({ values, handleChange, errors, setValues }) => (
              <Form>
                <SimpleTextbox
                  label="Membership Package Name"
                  type="text"
                  name="name"
                  value={values.name}
                  className="form-control"
                  onChange={handleChange}
                  required
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                    "Membership Package name is required"
                  )}
                />

                <SimpleTextbox
                  label="Description"
                  as="textarea"
                  name="description"
                  value={values.description}
                  className="form-control"
                  onChange={handleChange}
                  required
                  validationSchema={REQUIRED_FIELD_VALIDATION_SCHEMA(
                    "Package description is required"
                  )}
                />

                <SimpleTextbox
                  label="Credits"
                  type="number"
                  name="credits"
                  value={values.credits}
                  className="form-control"
                  onChange={handleChange}
                  min="0"
                  required
                />

                <SimpleTextbox
                  label="Price (£)"
                  type="number"
                  name="price"
                  value={values.price}
                  className="form-control"
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />

                <SimpleTextbox
                  label="Price per credit (£)"
                  type="number"
                  name="pricePerCredit"
                  value={values.pricePerCredit}
                  className="form-control"
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  required
                />

                <SimpleTextbox
                  label="Discount"
                  type="number"
                  name="discount"
                  value={values.discount || ""}
                  className="form-control"
                  onChange={handleChange}
                  required
                />
              </Form>
            )}
          </Formik>
        </Col>
        {membershipPackageData?.id && (
        <Col sm="12">
          <hr />
          <Button
            type="button"
            variant="outline-danger"
            className="px-4 text-danger mr-1"
            onClick={deleteMembershipPackage?.bind(null, membershipPackageData.id)}>
              Delete Membership Package
          </Button>
        </Col>
        )}
      </Row>
    </SimpleCard>
  );
};

export default MembershipPackageDetails;